import React, { useEffect, useState } from 'react';
import type { CrudEditModalProps } from '../crud/CrudTable';
import useLoadTracker from '@dr-pam/common-components/Hooks/useLoadTracker';
import { Container, TextInput, Button, Loader, Group, Select } from '@mantine/core';
import { useForm, schemaResolver } from '@mantine/form';
import { modals } from '@mantine/modals';
import { useArticleService } from '../../services/ArticleService';
import useComputedSlug from '../../hooks/useComputedSlug';
import type { AddArticleForm } from './AddArticleModal';
import type {
	ArticleListFragment,
	ArticleSingleFragment,
	ArticleUpdateInput,
	ResourceTypeListFragment,
} from '../../graphql/graphql';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';
import { useResourceService } from '../../services/ResourceService';
import TagsInput from '@dr-pam/common-components/Components/Form/TagsInput';
import { getUniqueTagNames } from '@dr-pam/common-components/Utils/TagUtils';
import { useTagService } from '../../services/TagService';
import z from 'zod';

export const validationSchema = z.object({
	title: z.string().nonempty().max(250),
	slug: z.string().nonempty().max(250),
	tags: z.array(z.string()).optional(),
});

export type EditArticleModalProps = CrudEditModalProps<ArticleListFragment, ArticleSingleFragment>;

export default function EditArticleModal(props: EditArticleModalProps) {
	const { modalId, onCancel, onEdited, current } = props;

	const { addLoader, removeLoader, isLoading } = useLoadTracker();

	const [resourceTypes, setResourceTypes] = useState<ResourceTypeListFragment[]>([]);

	const articleService = useArticleService();
	const resourceService = useResourceService();

	const tagService = useTagService();
	const [availableTags, setAvailableTags] = useState<string[]>([]);
	const [isLoadingTags, setIsLoadingTags] = useState(false);

	useEffect(() => {
		const loader = addLoader();
		const request = resourceService.getAllTypes();
		request.response
			.then((types) => {
				setResourceTypes(types);
			})
			.catch((err) => {
				NotificationUtils.showError(err as Error, 'Failed to load resource types');
			})
			.finally(() => {
				removeLoader(loader);
			});
	}, [addLoader, removeLoader, resourceService]);

	useEffect(() => {
		let cancelled = false;

		(async () => {
			setIsLoadingTags(true);

			try {
				const request = tagService.getAll();
				const allTags = await request.response;

				if (!cancelled) {
					setAvailableTags(allTags.map((t) => t.name));
				}
			} finally {
				if (!cancelled) {
					setIsLoadingTags(false);
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [tagService]);

	const form = useForm<EditArticleForm>({
		initialValues: {
			title: current.title,
			slug: current.slug,
			resourceTypeId: current.resourceTypeId,
			tags: current.articleTags?.map((at) => at.tag?.name).filter((n): n is string => !!n) ?? [],
		},
		validate: schemaResolver(validationSchema),
	});

	const handleSubmit = async (values: EditArticleForm) => {
		const loader = addLoader();

		try {
			const uniqueTags = getUniqueTagNames(values.tags ?? []);
			await articleService.update(current.id, {
				articleTags: {
					deleteMany: [
						{
							articleId: {
								equals: current.id,
							},
						},
					],
				},
			});

			const articleTagsCreate =
				uniqueTags.length > 0
					? {
							create: uniqueTags.map((tagName) => ({
								tag: {
									connectOrCreate: {
										where: { name: tagName },
										create: { name: tagName },
									},
								},
							})),
						}
					: undefined;

			const updateInput: ArticleUpdateInput = {
				title: { set: values.title },
				slug: { set: values.slug },
				resourceType: values.resourceTypeId
					? {
							connect: {
								id: values.resourceTypeId,
							},
						}
					: undefined,
				...(articleTagsCreate && { articleTags: articleTagsCreate }),
			};

			const updatedArticle = await articleService.update(current.id, updateInput);

			onEdited(updatedArticle);
			NotificationUtils.showSuccess('Successfully updated article', updatedArticle.title);
			modals.close(modalId);
		} catch (err) {
			NotificationUtils.showError(err as Error, 'Failed to update article');
			removeLoader(loader);
		}
	};

	const handleCancel = () => {
		onCancel?.();
		modals.close(modalId);
	};

	const slugComputedFromProps = useComputedSlug(form, 'slug', 'title');

	const resourceTypeData = resourceTypes.map((resourceType) => ({
		value: resourceType.id,
		label: resourceType.name,
	}));

	return (
		<Container>
			<form onSubmit={form.onSubmit(handleSubmit)}>
				<TextInput
					{...slugComputedFromProps}
					label="Title"
					description="The title of the article. Forms the basis of the Slug"
					disabled={isLoading}
					withAsterisk
				/>
				<TextInput
					{...form.getInputProps('slug')}
					mt="md"
					label="Slug"
					description="This will appear in the browser URL. Can't be changed."
					disabled={isLoading}
					withAsterisk
				/>
				<Select
					{...form.getInputProps('resourceTypeId')}
					data={resourceTypeData}
					label="Resource type"
					placeholder={isLoading ? 'Loading...' : '--- Please select ---'}
					disabled={isLoading}
				/>
				<TagsInput
					{...form.getInputProps('tags')}
					availableTags={availableTags}
					label="Tags"
					description="Add tags to categorize this article. Tags are shared across content."
					disabled={isLoading || isLoadingTags}
					placeholder={isLoadingTags ? 'Loading tags...' : 'Select or create tags'}
				/>
				<Group justify="flex-end" mt="md">
					<Button type="button" variant="subtle" onClick={handleCancel} disabled={isLoading}>
						Cancel
					</Button>
					<Button type="submit" disabled={isLoading}>
						Save article {isLoading && <Loader size="xs" ml="xs" />}
					</Button>
				</Group>
			</form>
		</Container>
	);
}

export type EditArticleForm = Omit<AddArticleForm, 'categoryId'> & {
	tags: string[];
};
